import React from "react";
import { SvgIcon } from "../../components/SvgComponents";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


interface VectorMotionInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const VectorMotionInsights: React.FC<VectorMotionInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Identify which bullet takeaway is currently active
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // ── Card entrance animation (spring zoom + fade) ──
  const cardScale = spring({
    frame: elapsedFrames,
    fps,
    config: { damping: 12, mass: 0.5, stiffness: 140 },
  });

  const cardOpacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // ── SVG float animation (sinusoidal offset) ──
  const floatY = Math.sin(globalFrame * 0.05) * 12;

  // ── Staggered word animation logic ──
  const words = activeBullet.text.split(" ");
  const wordDelay = 1.5; // delay in frames between words

  const isHighlighted = (word: string) => {
    if (!activeBullet.highlights) return false;
    const cleanWord = word.toLowerCase().replace(/[^a-z0-9%$-]/g, "");
    return activeBullet.highlights.some((h) => {
      const cleanHighlight = h.toLowerCase().replace(/[^a-z0-9%$-]/g, "");
      return cleanHighlight.includes(cleanWord) || cleanWord.includes(cleanHighlight);
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        gap: "30px",
      }}
    >
      {/* 1. Large SVG Showcase - Completely Borderless & Floating Freely */}
      <div
        style={{
          width: "100%",
          height: "380px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          opacity: cardOpacity,
          transform: `scale(${cardScale}) translateY(${floatY}px)`,
          overflow: "visible",
        }}
      >
        <div
          style={{
            filter: "drop-shadow(0 15px 35px rgba(200, 164, 255, 0.45))",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#ffffff"
                accentColor="#c8a4ff"
                size={220}
                style="glass"
              />
            ) : (
              <SvgIcon
                name={activeBullet.icon || "sparkles"}
                width={320}
                height={320}
                primaryColor="#c8a4ff"
              />
            );
          })()}
        </div>
      </div>

      {/* 2. Takeaway Pill Badge */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 16px",
          borderRadius: "50px",
          background: "rgba(255, 183, 213, 0.6)",
          border: "2px solid #ffb7d5",
          color: "#2d1b3d",
          fontFamily: "'Nunito', sans-serif",
          fontWeight: 800,
          fontSize: "13px",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
          boxShadow: "0 4px 15px rgba(255, 183, 213, 0.2)",
        }}
      >
        Takeaway {activeIndex + 1}
      </div>

      {/* 3. Staggered Word-by-Word Text Display */}
      <div
        style={{
          width: "100%",
          minHeight: "260px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignContent: "center",
          padding: "0 25px",
        }}
      >
        <p
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: "44px",
            fontWeight: 800,
            lineHeight: 1.4,
            color: "#2d1b3d", // Deep purple-brown for readability on light bg
            textAlign: "center",
            margin: 0,
          }}
        >
          {words.map((word, index) => {
            const wordStartFrame = bulletStartFrame + index * wordDelay;
            const wordElapsed = Math.max(0, globalFrame - wordStartFrame);

            const wordScale = spring({
              frame: wordElapsed,
              fps,
              config: { damping: 12, mass: 0.5, stiffness: 140 },
            });

            const wordOpacity = interpolate(wordElapsed, [0, 6], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });

            const wordTranslateY = interpolate(wordElapsed, [0, 8], [15, 0], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });

            const highlighted = isHighlighted(word);

            return (
              <span
                key={index}
                style={{
                  display: "inline-block",
                  marginRight: "10px",
                  opacity: wordOpacity,
                  transform: `scale(${wordScale}) translateY(${wordTranslateY}px)`,
                }}
              >
                {highlighted ? (
                  <strong
                    style={{
                      fontWeight: 800,
                      background: "linear-gradient(135deg, #ff9bbd, #c8a4ff)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      display: "inline",
                    }}
                  >
                    {word}
                  </strong>
                ) : (
                  <span>{word}</span>
                )}
              </span>
            );
          })}
        </p>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
